import React from 'react';
import { MapPin, Clock, Navigation, Phone } from 'lucide-react';
import { contactData } from '../data/mockData';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const ContactSection = () => {
  return (
    <section id="contact" className="py-20 md:py-32 relative">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg h-[400px] bg-primary/5 blur-[150px] rounded-full pointer-events-none z-0" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
        <AnimateOnScroll>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold uppercase tracking-tight mb-4 italic">
            {contactData.headingPart1}{' '}
            <span className="gold-gradient">{contactData.headingHighlight}</span>
          </h2>
        </AnimateOnScroll>

        <AnimateOnScroll delay={100}>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto mb-8">
            {contactData.subtitle}
          </p>
        </AnimateOnScroll>

        {/* Shop Front Image */}
        <AnimateOnScroll delay={150}>
          <div className="relative group rounded-3xl overflow-hidden border border-white/10 w-full max-w-md mx-auto aspect-square mb-14 sm:mb-16">
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80 z-10 transition-opacity duration-500 group-hover:opacity-100" />
            <img
              src="https://customer-assets.emergentagent.com/job_scissors-shop-1/artifacts/vmi88bun_11.png"
              alt="Golden Scissors shop front, Market Harborough"
              className="w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute bottom-0 left-0 p-6 md:p-8 z-20 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 w-full">
              <p className="font-heading text-primary font-bold text-xl md:text-2xl uppercase mb-1 italic">
                Golden Scissors
              </p>
              <p className="text-zinc-300 text-sm">
                Traditional Turkish Barbers
              </p>
            </div>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll delay={200}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {/* Location Card */}
            <div className="bg-card border border-white/10 rounded-3xl p-8 flex flex-col justify-center items-center md:items-start text-center md:text-left hover:border-primary/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-6 text-primary">
                <MapPin className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3 uppercase italic">
                {contactData.location.title}
              </h3>
              <p className="text-zinc-400 leading-relaxed">
                {contactData.location.lines.map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < contactData.location.lines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            </div>

            {/* Phone Card */}
            <div className="bg-card border border-white/10 rounded-3xl p-8 flex flex-col items-center md:items-start text-center md:text-left hover:border-primary/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-6 text-primary">
                <Phone className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3 uppercase italic">
                {contactData.phone.title}
              </h3>
              <a
                href={`tel:${contactData.phone.number}`}
                className="text-zinc-400 leading-relaxed hover:text-primary transition-colors"
              >
                {contactData.phone.display}
              </a>
            </div>

            {/* Hours Card */}
            <div className="bg-card border border-white/10 rounded-3xl p-8 flex flex-col justify-center items-center md:items-start text-center md:text-left hover:border-primary/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-6 text-primary">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3 uppercase italic">
                {contactData.hours.title}
              </h3>
              <p className="text-zinc-400 leading-relaxed">
                {contactData.hours.lines.map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < contactData.hours.lines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            </div>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll delay={300}>
          <a
            href={contactData.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 sm:mt-12 inline-flex items-center justify-center w-full sm:w-auto bg-primary text-black font-bold py-4 px-8 rounded-full hover:bg-yellow-400 hover:scale-105 transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] gap-3 text-sm sm:text-base"
          >
            <Navigation className="w-5 h-5" />
            <span>{contactData.mapLabel}</span>
          </a>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default ContactSection;
